<template>
  <div :class="['tw-flex tw-justify-between tw-items-center tw-py-2.5 tw-px-4 sm:tw-py-4 sm:tw-px-6', { 'tw-border-b': isLast === false }]">
    <div>
      <div class="tw-flex tw-items-center">
        <div class="tw-text-gray-800 tw-text-md">{{ localCharge.description }}</div>
        <base-badge size="sm" class="tw-ml-2" :color="getStatusColor(localCharge.status)">{{ capitalize(localCharge.status) }}</base-badge>
      </div>
      <div class="tw-text-gray-500 tw-text-xs tw-leading-none">{{ formatDate(charge.createdDate) }}</div>
    </div>
    <div class="tw-flex tw-items-center tw-gap-2">
      <Money-Format :value="localCharge.amount" :locale="locale" :currency-code="localCharge.currency" subunits-value class="tw-mr-3" />
      <base-button v-if="isGenerating === true" type="secondary" disabled>
        <template #icon>
          <base-spinner :size="16" current class="tw-mr-2" />
        </template>
        Generating PDF...
      </base-button>
      <base-button v-if="isGenerating === false && !localCharge.documentId" @click="generateInvoicePdf()" :loading="isSendingGenerateInvoicePdf" type="flat">
        <template #icon>
          <pdf-icon />
        </template>
        Generate PDF
      </base-button>
      <base-button v-if="isGenerating === false && localCharge.documentId" @click="downloadInvoicePdf()" :loading="isDownloading" type="flat">
        <template #icon>
          <download-icon />
        </template>
        Download PDF
      </base-button>
    </div>
  </div>
</template>
<script>
import moment from "moment";
import MoneyFormat from "vue-money-format";
import { orderBy, capitalize } from "lodash";
import { DownloadIcon, PdfIcon } from "@/assets/svgs";
import BaseSpinner from "@/components/base/BaseSpinner";

export default {
  components: {
    MoneyFormat,
    DownloadIcon,
    PdfIcon,
    BaseSpinner,
  },
  props: {
    userId: String,
    userBusinessPlanId: String,
    charge: Object,
    isLast: { type: Boolean, default: false },
  },
  data() {
    return {
      isSendingGenerateInvoicePdf: false,
      isDownloading: false,
      isGenerating: false,
      intervalId: null,
      localCharge: { ...this.charge },
    };
  },
  computed: {
    locale() {
      return window.navigator.language;
    },
    orderedCharges() {
      return orderBy(this.charges, ["createdDate"], ["desc"]);
    },
  },
  watch: {
    isGenerating: {
      handler(isGenerating) {
        if (isGenerating && this.localCharge.documentId === null) {
          this.intervalId = setInterval(this.checkPDF, 5000);
        } else if (this.intervalId) {
          clearInterval(this.intervalId);
          this.intervalId = null;
        }
      },
      immediate: true,
    },
    charge: {
      handler(newCharge) {
        this.localCharge = { ...newCharge };
      },
      deep: true,
    },
  },
  methods: {
    capitalize,
    formatDate(date) {
      return new moment.utc(date).local().format("MMM Do, YYYY h:mm A");
    },
    getStatusColor(status) {
      switch (status) {
        case "succeeded":
          return "green";
        case "pending":
          return "yellow";
        case "failed":
          return "red";
      }
    },
    async generateInvoicePdf() {
      this.isSendingGenerateInvoicePdf = true;
      await this.$axios
        .post(`/businesses/${this.localCharge.businessId}/users/${this.localCharge.userId}/charges/${this.localCharge.chargeId}/invoice/pdf`)
        .then(() => {
          this.isGenerating = true;
          this.EventBus.$emit("GLOBAL_NOTIFY", { title: "We are working on your PDF", message: "This can sometimes take up to 2 minutes." });
        })
        .finally(() => {
          this.isSendingGenerateInvoicePdf = false;
        });
    },
    async downloadInvoicePdf() {
      try {
        this.isDownloading = true;
        const documentResponse = await this.$axios.get(`/documents/${this.localCharge.documentId}`);
        const downloadResponse = await this.$axios.get(documentResponse.data.downloadUrl, { responseType: "blob" });

        var blob = new Blob([downloadResponse.data]);
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = documentResponse.data.name + "." + documentResponse.data.extension;
        link.click();
        URL.revokeObjectURL(link.href);
      } catch (error) {
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Failed to download document",
          message: "We had a problem retrieving the document. Please reload the page and try again.",
          error,
        });
      } finally {
        this.isDownloading = false;
      }
    },
    checkPDF() {
      this.$axios.get(`/businesses/${this.localCharge.businessId}/users/${this.localCharge.userId}/charges/${this.localCharge.chargeId}/invoice/pdf`).then((response) => {
        if (response.data.documentId !== null) {
          this.localCharge.documentId = response.data.documentId;
          this.isGenerating = false;
          this.EventBus.$emit("GLOBAL_NOTIFY", { title: "PDF is ready", message: "You can now download your PDF." });
        }
      });
    },
  },
};
</script>
